import { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "admin" | "employee" | "customer";

interface Profile {
  id: string;
  full_name: string;
  cpf: string | null;
  phone: string | null;
  email: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  neighborhood: string | null;
  address_number: string | null;
  address_complement: string | null;
  address_reference: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  loading: boolean;
  hasRole: (role: AppRole) => boolean;
  isAdmin: boolean;
  isEmployee: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Defer fetching to avoid deadlock
          setTimeout(async () => {
            const [{ data: profileData }, { data: rolesData }] = await Promise.all([
              supabase
                .from("profiles")
                .select("id, full_name, cpf, phone, email, street, city, state, zip_code, neighborhood, address_number, address_complement, address_reference")
                .eq("user_id", session.user.id)
                .single(),
              supabase
                .from("user_roles")
                .select("role")
                .eq("user_id", session.user.id),
            ]);
            setProfile(profileData);
            setRoles((rolesData ?? []).map((r) => r.role as AppRole));
            setLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const hasRole = useCallback((role: AppRole) => roles.includes(role), [roles]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const isAdmin = useMemo(() => roles.includes("admin"), [roles]);
  const isEmployee = useMemo(() => roles.includes("employee"), [roles]);

  const value = useMemo<AuthContextType>(
    () => ({ user, session, profile, roles, loading, hasRole, isAdmin, isEmployee, signOut }),
    [user, session, profile, roles, loading, hasRole, isAdmin, isEmployee, signOut]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
